import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import InputDepartureSelect from '~/components/Cruises/components/Selects/InputDepartureSelect';
import InputRateCodeSelect from '~/components/Cruises/components/Selects/InputRateCodeSelect';
import InputRateGroupMultiselect from '~/components/Cruises/components/Selects/InputRateGroupMultiselect';

import { useGetLuxPlusPricingConfigs } from '~/hooks/apis/cruises/useGetLuxPlusPricingConfigs';
import {
  useCreateLuxPlusPricingConfig,
  useDeleteLuxPlusPricingConfig,
  useUpdateLuxPlusPricingConfigEnabled,
} from '~/hooks/apis/cruises/useLuxPlusPricingConfigMutations';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import {
  CreateLuxPlusPricingConfigBody,
  LuxPlusPricingConfig,
  LuxPlusPricingFilter,
  LuxPlusPricingMode,
} from '~/services/cruises/LuxPlusPricingConfigService';

const INITIAL_FORM: Omit<CreateLuxPlusPricingConfigBody, 'vendorId'> = {
  pricingFilter: 'ALL',
  pricingMode: 'PERCENTAGE',
  isTotalBasedDiscount: false,
};

type PricingConfigForm = Omit<typeof INITIAL_FORM, 'discountPercent'> & {
  discountPercentage?: number;
};

const FILTER_LABELS: Record<LuxPlusPricingFilter, string> = {
  ALL: 'All rates',
  RATE: 'Rate',
  RATE_GROUP: 'Rate group',
  DEPARTURE_RATE: 'Departure and rate',
};

const MODE_LABELS: Record<LuxPlusPricingMode, string> = {
  PERCENTAGE: 'Percentage discount',
  STANDARD_PRICE: 'Standard price',
};

function formatDiscountPercentage(discountPercent?: number): string {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 4 }).format((discountPercent ?? 0) * 100);
}

export function toDiscountPercent(discountPercentage?: number): number | undefined {
  return discountPercentage === undefined ? undefined : Math.round(discountPercentage * 100) / 10_000;
}

function LuxPlusPricingPage() {
  const [cruiseLine, setCruiseLine] = useState<CruiseLine>();
  const [form, setForm] = useState<PricingConfigForm>(INITIAL_FORM);

  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const { data, isLoading } = useGetLuxPlusPricingConfigs(cruiseLine?.id);
  const createMutation = useCreateLuxPlusPricingConfig();
  const updateMutation = useUpdateLuxPlusPricingConfigEnabled(cruiseLine?.id);
  const deleteMutation = useDeleteLuxPlusPricingConfig(cruiseLine?.id);

  const configs = data?.result ?? [];
  const isSubmitting = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isRateFilter = form.pricingFilter === 'RATE' || form.pricingFilter === 'DEPARTURE_RATE';

  const isFormValid = useMemo(() => {
    if (!cruiseLine) {
      return false;
    }
    if (form.pricingFilter === 'RATE' && !form.rateId) {
      return false;
    }
    if (form.pricingFilter === 'RATE_GROUP' && !form.rateGroupId) {
      return false;
    }
    if (form.pricingFilter === 'DEPARTURE_RATE' && (!form.rateId || !form.departureId)) {
      return false;
    }
    if (form.pricingMode === 'STANDARD_PRICE') {
      return form.pricingFilter !== 'ALL';
    }

    return (
      form.discountPercentage !== undefined &&
      form.discountPercentage > 0 &&
      form.discountPercentage <= 100 &&
      form.minSavingAud !== undefined &&
      form.minSavingAud >= 0
    );
  }, [cruiseLine, form]);

  const resetForm = useCallback(() => setForm(INITIAL_FORM), []);

  const handleCruiseLineChange = useCallback(
    (selected: CruiseLine) => {
      setCruiseLine(selected);
      resetForm();
    },
    [resetForm],
  );

  const handleFilterChange = useCallback((pricingFilter: LuxPlusPricingFilter) => {
    setForm((current) => ({
      ...current,
      pricingFilter,
      rateId: undefined,
      rateGroupId: undefined,
      departureId: undefined,
    }));
  }, []);

  const handleModeChange = useCallback((pricingMode: LuxPlusPricingMode) => {
    setForm((current) => ({
      ...current,
      pricingMode,
      discountPercentage: undefined,
      minSavingAud: undefined,
      isTotalBasedDiscount: false,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!cruiseLine || !isFormValid) {
      return;
    }

    try {
      const { discountPercentage, ...config } = form;
      await createMutation.mutateAsync({
        vendorId: cruiseLine.id,
        ...config,
        discountPercent: toDiscountPercent(discountPercentage),
      });
      enqueueSnackbar('LuxPlus pricing configuration created successfully', { variant: 'success' });
      resetForm();
    } catch {
      enqueueSnackbar('Error creating LuxPlus pricing configuration', { variant: 'error' });
    }
  }, [createMutation, cruiseLine, enqueueSnackbar, form, isFormValid, resetForm]);

  const handleEnabledChange = useCallback(
    async (config: LuxPlusPricingConfig, enabled: boolean) => {
      try {
        await updateMutation.mutateAsync({ id: config.id, enabled });
        enqueueSnackbar('LuxPlus pricing configuration updated successfully', { variant: 'success' });
      } catch {
        enqueueSnackbar('Error updating LuxPlus pricing configuration', { variant: 'error' });
      }
    },
    [enqueueSnackbar, updateMutation],
  );

  const handleDelete = useCallback(
    async (config: LuxPlusPricingConfig) => {
      const confirmed = await showConfirmDialog({
        title: 'Delete LuxPlus Pricing Configuration?',
        description: 'This will permanently delete the pricing configuration.',
      });
      if (!confirmed) {
        return;
      }

      try {
        await deleteMutation.mutateAsync(config.id);
        enqueueSnackbar('LuxPlus pricing configuration deleted', { variant: 'success' });
      } catch {
        enqueueSnackbar('Error deleting LuxPlus pricing configuration', { variant: 'error' });
      }
    },
    [deleteMutation, enqueueSnackbar, showConfirmDialog],
  );

  return (
    <Box>
      <Helmet>
        <title>LuxPlus Pricing</title>
      </Helmet>
      <PageHeader title="LuxPlus Pricing">
        <Button
          variant="text"
          startIcon={<HelpOutlineIcon />}
          href="https://aussiecommerce.atlassian.net/wiki/spaces/CRUIS/pages/5124292633/Cruises+LP+Configurations"
          target="_blank"
          rel="noopener noreferrer"
        >
          Documentation
        </Button>
      </PageHeader>

      <Stack spacing={3}>
        <InputCruiseLineSelect
          label="Cruise line"
          value={cruiseLine?.id ?? ''}
          onChange={handleCruiseLineChange}
          withApprovedOffers
          disabled={isSubmitting}
        />

        {cruiseLine && (
          <>
            <Divider />
            <Typography variant="h6">Add pricing configuration</Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="luxplus-pricing-filter-label">Pricing filter</InputLabel>
                <Select
                  labelId="luxplus-pricing-filter-label"
                  label="Pricing filter"
                  value={form.pricingFilter}
                  disabled={isSubmitting}
                  onChange={(event) => handleFilterChange(event.target.value)}
                >
                  {Object.entries(FILTER_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="luxplus-pricing-mode-label">Pricing mode</InputLabel>
                <Select
                  labelId="luxplus-pricing-mode-label"
                  label="Pricing mode"
                  value={form.pricingMode}
                  disabled={isSubmitting}
                  onChange={(event) => handleModeChange(event.target.value)}
                >
                  {Object.entries(MODE_LABELS)
                    .filter(([value]) => value !== 'STANDARD_PRICE' || form.pricingFilter !== 'ALL')
                    .map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {isRateFilter && (
                <InputRateCodeSelect
                  key={form.pricingFilter}
                  selectedRateIds={form.rateId ? [form.rateId] : []}
                  onChange={(rateIds) => setForm((current) => ({ ...current, rateId: rateIds[0] }))}
                  vendorCode={cruiseLine.code}
                  isLoading={isSubmitting}
                  maxSelections={1}
                />
              )}

              {form.pricingFilter === 'RATE_GROUP' && (
                <InputRateGroupMultiselect
                  label="Rate group"
                  rateGroupIds={form.rateGroupId ? [form.rateGroupId] : []}
                  vendorId={cruiseLine.id}
                  onChange={(rateGroups) => setForm((current) => ({ ...current, rateGroupId: rateGroups[0]?.id }))}
                  disabled={isSubmitting}
                  maxSelections={1}
                />
              )}

              {form.pricingFilter === 'DEPARTURE_RATE' && (
                <InputDepartureSelect
                  selectedDepartureIds={form.departureId ? [form.departureId] : []}
                  onChange={(departureIds) => setForm((current) => ({ ...current, departureId: departureIds[0] }))}
                  vendorId={cruiseLine.id}
                  maxSelections={1}
                />
              )}

              {form.pricingMode === 'PERCENTAGE' && (
                <>
                  <TextField
                    label="Discount percentage"
                    type="number"
                    required
                    inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                    placeholder="e.g. 5"
                    helperText="Enter 5 for a 5% discount"
                    value={form.discountPercentage ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        discountPercentage: event.target.value === '' ? undefined : Number(event.target.value),
                      }))
                    }
                  />
                  <TextField
                    label="Minimum saving (AUD)"
                    type="number"
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    value={form.minSavingAud ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, minSavingAud: Number(event.target.value) }))
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isTotalBasedDiscount ?? false}
                        onChange={(event) =>
                          setForm((current) => ({ ...current, isTotalBasedDiscount: event.target.checked }))
                        }
                      />
                    }
                    label="Calculate discount from the total price"
                  />
                </>
              )}

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={resetForm} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!isFormValid || isSubmitting}>
                  {createMutation.isPending ? <Spinner size={15} inline /> : 'Add'}
                </Button>
              </Stack>
            </Stack>

            <Divider />
            <Typography variant="h6">Pricing configurations</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Filter</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>{FILTER_LABELS[config.pricingFilter]}</TableCell>
                    <TableCell>{MODE_LABELS[config.pricingMode]}</TableCell>
                    <TableCell>
                      {config.departure
                        ? `${config.departure.externalId} - ${config.departure.name} / ${config.rate?.code}`
                        : (config.rate?.code ?? config.rateGroup?.name ?? 'All rates')}
                    </TableCell>
                    <TableCell>
                      {config.pricingMode === 'PERCENTAGE'
                        ? `${formatDiscountPercentage(config.discountPercent)}% (min A$${config.minSavingAud})`
                        : 'Rate price'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={config.enabled}
                        disabled={isSubmitting}
                        onChange={(event) => handleEnabledChange(config, event.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" disabled={isSubmitting} onClick={() => handleDelete(config)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && configs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No LuxPlus pricing configurations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </Stack>
    </Box>
  );
}

export default LuxPlusPricingPage;
